/**
 * A board's server-sent event stream: what lets the page show an automation's
 * result the moment the worker finishes, rather than refetching on a timer and
 * hoping the worker was done. EventSource cannot send the workspace header or
 * the local bearer token, so the stream is read through `fetchWithAuth`.
 *
 * Which boards have a live stream is kept here, outside React, so the edit
 * paths can drop their fallback timers while one is open.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { z } from 'zod';

import { getWorkspaceId } from '@/entities/workspace';
import { fetchWithAuth } from '@/lib/api/fetch-with-auth';
import { ENV } from '@/lib/config/env';

const automationRunEventSchema = z.object({
  type: z.literal('automation.run'),
  rowRef: z.string(),
  columns: z.array(z.string()),
  runs: z.array(z.object({ automationId: z.string(), outcome: z.string() })),
  rowsChanged: z.literal(true).optional(),
});
export type AutomationRunEvent = z.infer<typeof automationRunEventSchema>;

const RETRY_FIRST_MS = 1_000;
const RETRY_MAX_MS = 30_000;

const liveBoards = new Set<string>();
const liveListeners = new Set<() => void>();

function setLive(trackerId: string, live: boolean): void {
  if (live === liveBoards.has(trackerId)) return;
  if (live) liveBoards.add(trackerId);
  else liveBoards.delete(trackerId);
  liveListeners.forEach((notify) => notify());
}

/** Whether this board's stream is open right now. */
export function useTrackerStreamLive(trackerId: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      liveListeners.add(notify);
      return () => liveListeners.delete(notify);
    },
    () => liveBoards.has(trackerId),
  );
}

/** Splits a server-sent event block into its event name and parsed data. */
export function parseSseBlock(block: string): { event: string; data: unknown } | null {
  // eslint-disable-next-line i18next/no-literal-string -- the SSE default event name
  let event = 'message';
  const data: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
  }
  if (data.length === 0) return null;
  try {
    return { event, data: JSON.parse(data.join('\n')) as unknown };
  } catch {
    return null;
  }
}

/**
 * Holds the board's stream open while mounted and hands each automation result
 * to `onRun`. The board counts as live from the server's `ready` until the
 * stream drops or the server sends `close`; it then reconnects with a growing
 * pause, and the timers cover the gap.
 */
export function useTrackerEvents(
  trackerId: string,
  onRun: (event: AutomationRunEvent) => void,
): void {
  const onRunRef = useRef(onRun);
  onRunRef.current = onRun;

  useEffect(() => {
    if (!trackerId) return;
    const controller = new AbortController();
    let retryMs = RETRY_FIRST_MS;

    const readOnce = async () => {
      const workspaceId = getWorkspaceId();
      const base = ENV.WORKSPACE_API_BASE_URL.trim().replace(/\/+$/, '');
      const response = await fetchWithAuth(`${base}/api/v1/trackers/${trackerId}/events`, {
        headers: {
          Accept: 'text/event-stream',
          ...(workspaceId ? { 'x-selected-workspace-id': workspaceId } : {}),
        },
        credentials: 'include',
        signal: controller.signal,
      });
      if (!response.ok || !response.body) return;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) return;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
        let end = buffer.indexOf('\n\n');
        while (end !== -1) {
          const parsed = parseSseBlock(buffer.slice(0, end));
          buffer = buffer.slice(end + 2);
          end = buffer.indexOf('\n\n');
          // Open headers do not mean the server is subscribed to the board;
          // until it says so, the edit paths keep their timers.
          if (parsed?.event === 'ready') {
            setLive(trackerId, true);
            retryMs = RETRY_FIRST_MS;
            continue;
          }
          // The server ends a stream when the viewer's access lapses or the
          // stream reaches its lifetime cap; reconnecting re-authorises them.
          if (parsed?.event === 'close') {
            await reader.cancel();
            return;
          }
          if (parsed?.event !== 'automation.run') continue;
          const event = automationRunEventSchema.safeParse(parsed.data);
          if (event.success) onRunRef.current(event.data);
        }
      }
    };

    const run = async () => {
      while (!controller.signal.aborted) {
        try {
          await readOnce();
        } catch {
          // Dropped or refused; the pause below decides when to try again.
        }
        setLive(trackerId, false);
        await new Promise((resolve) => setTimeout(resolve, retryMs));
        retryMs = Math.min(retryMs * 2, RETRY_MAX_MS);
      }
    };
    void run();

    return () => {
      controller.abort();
      setLive(trackerId, false);
    };
  }, [trackerId]);
}
